import * as LoginActions from 'auth/actions';
import { getLoggedInStatus, getUsername } from 'auth/selectors';
import { bind } from 'bind-decorator';
import * as classNames from 'classnames';
import { Icon } from 'elements';
import { UsersApi } from 'global/api';
import { ROUTES } from 'global/constants';
import { RootState } from 'global/state';
import { localeInfo } from 'i18n';
import { AccountDetails } from 'labxchange-client';
import * as moment from 'moment-timezone';
import * as React from 'react';
import { MessageDescriptor } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { NavLink } from 'react-router-dom';
import { countries } from 'typed-countries';
import { UI_IS_SM } from 'ui/breakpoints';
import {
    Button,
    CalendarDate,
    Collapsible,
    DateInput,
    LocaleSwitcher,
    ModalConfirmation,
    StandardPageLayout,
} from 'ui/components';
import uiMessages from 'ui/components/displayMessages';
import { showErrorMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import { WrappedMessage } from 'utils';
import { NotificationSettingsComponent } from './NotificationSettingsComponent';
import messages from './displayMessages';
import { RegistrationGate } from 'ui/components/RegistrationGate';
import { intl } from 'i18n';

// This will be used to format the output from CalendarDate,
// so much specify the UTC timezone.
const dateFormatter = new Intl.DateTimeFormat(localeInfo.key, {month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'});

interface SettingsSectionProps {
    id: string;
    title: MessageDescriptor;
    children?: React.ReactNode;
}

interface SettingsSectionState {
    isCollapsible: boolean;
    isOpen: boolean;
}

export class SettingsSection extends React.Component<SettingsSectionProps, SettingsSectionState> {
    public constructor(props: SettingsSectionProps) {
        super(props);
        this.state = {
            isCollapsible: this.isMobileView(),
            isOpen: !this.isMobileView(),
        };
    }

    public componentDidMount() {
        window.addEventListener('resize', this.onResize);
        this.onResize();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    public render() {
        let indicator = null;
        if (this.state.isCollapsible) {
            indicator = (
                <Icon
                    className='settings-section-indicator'
                    name={this.state.isOpen ? 'chevron-up' : 'chevron-down'}
                    zoom='1.4em'
                />
            );
        }

        const title = (
            <div className='info-heading'>
                <h2 className='section-title'>
                    <WrappedMessage message={this.props.title} />
                    {indicator}
                </h2>
            </div>
        );

        return (
            <div id={this.props.id} ref={this.props.id} className='info-box'>
                <Collapsible
                    title={title}
                    isOpen={this.state.isOpen}
                    isCollapsible={this.state.isCollapsible}
                    onToggle={this.onToggle}
                >
                    {this.props.children}
                </Collapsible>
            </div>
        );
    }

    @bind private onToggle() {
        this.setState({
            isOpen: !this.state.isOpen,
        });
    }

    @bind private isMobileView() {
        return UI_IS_SM.matches;
    }

    @bind private onResize() {
        this.setState({
            isCollapsible: this.isMobileView(),
        });
    }
}

enum DeleteAccountModal {
    None = 0,
    ConfirmDeleteFirst,
    ConfirmDeleteFinal,
    PerformDeleteAccount,
}

interface ReduxStateProps {
    loggedInUsername: string;
    isLoggedIn: boolean;
}

interface Props extends RouteComponentProps<{}>, ReduxStateProps {}

interface State {
    loading: boolean;
    userAccountDetails?: AccountDetails;
    toggleDateOfBirthEdit: boolean;
    deleteAccountModal: DeleteAccountModal;
    timeZoneValue: string | undefined;
    dateOfBirthValue?: CalendarDate;
    newDateOfBirthValue?: CalendarDate;
}

export class UserProfileSettingsInternal extends React.PureComponent<Props, State> {

    constructor(props: any) {
        super(props);
        this.state = {
            loading: true,
            toggleDateOfBirthEdit: false,
            deleteAccountModal: DeleteAccountModal.None,
            timeZoneValue: undefined,
        };
    }

    public async componentDidMount() {
        await this.loadData();
    }

    public async componentDidUpdate(prevProps: Props) {
        if (prevProps.isLoggedIn !== this.props.isLoggedIn) {
            this.setState({userAccountDetails: undefined});
            await this.loadData();
        }
    }

    public async loadData() {
        try {
            const result = await UsersApi.accountDetail();
            const unformattedTZ = result.timezone ? result.timezone : null;
            if (unformattedTZ) {
                // Use the moment-timezone package to format the timezone. For example, (GMT+00:00)Africa/Timbuktu.
                const timezone = (' (GMT' + moment.tz(unformattedTZ!).format('Z') + ')' + unformattedTZ!);
                this.setState({timeZoneValue: timezone});
            }
            this.setState({
                loading: false,
                userAccountDetails: result,
                dateOfBirthValue: result.dateOfBirth ? CalendarDate.fromJsDate(result.dateOfBirth) : undefined,
            });
        } catch (err) {
            this.setState({loading: false});
        }
    }

    public render() {
        if (this.state.userAccountDetails === undefined) {
            if (this.state.loading) {
                return (
                    <StandardPageLayout headerFeature={this.renderHeader()}>
                        <p><WrappedMessage message={uiMessages.uiLoading}/></p>
                    </StandardPageLayout>
                );
            } else {
                // We weren't able to find the user profile
                return <RegistrationGate
                    title={intl.formatMessage(messages.notLoggedInSettingsAlertTitle)}
                    body={intl.formatMessage(messages.notLoggedInSettingsAlertBody)}
                    primaryButtonText={uiMessages.uiSignUp}
                    secondaryButtonText={uiMessages.uiSignIn}
                    image='/assets/images/access.svg'
                />;
            }
        }

        let countryName;

        if ( this.state.userAccountDetails.country ) {
            const countryCode = this.state.userAccountDetails.country;
            const countryObj = countries.find((c: any) => c.iso === countryCode);
            countryName = countryObj!.name;
        } else {
            countryName = null;
        }

        return (
            <StandardPageLayout
                pageTitle={messages.pageTitle}
                containerClassName='container-fluid'
                otherClassName='account-settings-container p-0'
                headerFeature={this.renderHeader()}
            >
                <div className='account-settings-wrapper white-box'>
                    <div className='side-bar'>
                        <div className='profile-icon'>
                        </div>
                        <div className='section-nav'>
                            <nav>
                                <div className={classNames(
                                    'section',
                                    {active: this.props.location.hash === '#basic-info' ||
                                                 this.props.location.hash === ''})}>
                                    <a href='#basic-info' className='nav-item text-header'>
                                        <WrappedMessage message={messages.BasicAccountInfoTitle}/>
                                    </a>
                                </div>
                                <div className={classNames(
                                    'section',
                                    {active: this.props.location.hash === '#reset-password'})}>
                                    <a href='#reset-password' className='nav-item text-header'>
                                        <WrappedMessage message={messages.ResetPasswordTitle}/>
                                    </a>
                                </div>
                                <div className={classNames(
                                    'section',
                                    {active: this.props.location.hash === '#notifications'})}>
                                    <a href='#notifications' className='nav-item text-header'>
                                        <WrappedMessage message={messages.notificationSettingsTitle}/>
                                    </a>
                                </div>
                                <div className={classNames(
                                    'section',
                                    {active: this.props.location.hash === '#delete-account'})}>
                                    <a href='#delete-account' className='nav-item text-header'>
                                        <WrappedMessage message={messages.DeleteAccountSideBarText}/>
                                    </a>
                                </div>
                            </nav>
                        </div>
                    </div>
                    <div className='content-bar'>
                        <SettingsSection id='basic-info' title={messages.BasicAccountInfoTitle}>
                            <div className='basic-info-details'>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.UsernameTitle}/></small>
                                    <div className={classNames('value', 'username')}>
                                        {this.props.loggedInUsername}
                                    </div>
                                </div>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.FullnameTitle}/></small>
                                    <div className={'value full-name'}>
                                        {this.state.userAccountDetails.fullName
                                            ? this.state.userAccountDetails.fullName : null}
                                    </div>
                                </div>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.EmailTitle}/></small>
                                    <div className={'value email'}>
                                        {this.state.userAccountDetails.email ?
                                            this.state.userAccountDetails.email : null}
                                    </div>
                                </div>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.CountryTitle}/></small>
                                    <div className={classNames('value', 'country')}>
                                        {countryName}
                                    </div>
                                </div>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.TimezoneTitle}/></small>
                                    <div className={'value timezone'}>
                                        {this.state.timeZoneValue ? this.state.timeZoneValue : null}
                                    </div>
                                </div>
                                <div className='info-input'>
                                    <small><WrappedMessage message={messages.dateOfBirthTitle}/></small>
                                    {this.state.toggleDateOfBirthEdit
                                    ?
                                        <>
                                            <DateInput
                                                label={messages.dateOfBirthTitle}
                                                hiddenLabel={true}
                                                defaultValue={this.state.dateOfBirthValue}
                                                onChange={this.handleChangeDateOfBirth}
                                            />
                                            <Button
                                                btnStyle='outline'
                                                label={uiMessages.uiCancelButton}
                                                onClick={this.cancelDateOfBirthEdit}
                                            />
                                            <Button
                                                btnStyle='primary'
                                                label={uiMessages.uiSave}
                                                disabled={this.state.newDateOfBirthValue === undefined}
                                                onClick={this.handleSaveDateOfBirth}
                                            />
                                        </>
                                    :   <div className={'value date-of-birth'}>
                                            {this.state.dateOfBirthValue &&
                                                dateFormatter.format(this.state.dateOfBirthValue.toJsDate())}
                                            <button
                                                className={'unstyled edit-icon'}
                                                onClick={this.showDateOfBirthEdit}
                                                aria-label={intl.formatMessage(messages.ariaEditDateOfBirthButton)}>
                                                <Icon name='pencil' zoom='16'/>
                                            </button>
                                        </div>
                                    }
                                </div>
                                {!this.state.toggleDateOfBirthEdit ?
                                    <Button
                                        className='update-account-button'
                                        btnStyle='normal'
                                        newWindow={true}
                                        href='https://account.edx.org/#basic-information'
                                        label={messages.updateButton}
                                    />
                                : null /* Hide "Update Information" button when editing birthdate to avoid confusion */}
                            </div>
                        </SettingsSection>
                        <SettingsSection id='language-switcher' title={messages.LanguageSwitcherTitle}>
                            <div className='language-switcher-details'>
                                <p className='pb-2'><WrappedMessage message={messages.currentLanguage} /></p>
                                {localeInfo.nativeName}{' '}
                                <LocaleSwitcher btnType='text' />
                            </div>
                        </SettingsSection>
                        <SettingsSection id='reset-password' title={messages.ResetPasswordTitle}>
                            <div className='reset-password-section'>
                                <Button
                                    className='reset-password-button'
                                    btnStyle='normal'
                                    newWindow={true}
                                    href='https://account.edx.org/#basic-information'
                                    label={messages.ResetPasswordTitle}
                                />
                            </div>
                        </SettingsSection>
                        <SettingsSection id='notifications' title={messages.notificationSettingsTitle}>
                            <div className='notification-settings-section'>
                                <NotificationSettingsComponent />
                            </div>
                        </SettingsSection>
                        <SettingsSection id='delete-account' title={messages.DeleteAccountTitle}>
                            <div className='delete-text-section'>
                                <div className='delete-wrapper'>
                                    <p>
                                        <WrappedMessage message={messages.deleteMessageLine1}/>
                                        <NavLink to={ROUTES.Users.PROFILE_SLUG(this.props.loggedInUsername)} >
                                            <WrappedMessage message={messages.profileLink} />
                                        </NavLink>
                                    </p>
                                    <p><WrappedMessage message={messages.deleteMessageLine2}/></p>
                                    <p><WrappedMessage message={messages.deleteMessageLine3}/></p>
                                    <a href='#basic-info'>
                                        <WrappedMessage message={messages.deleteMessageLine4}/>
                                    </a>
                                    <div className='warning-box'>
                                        <span className='warning-icon'></span>
                                        <WrappedMessage message={messages.deleteWarningText1}/>
                                    </div>
                                    <div className='warning-box'>
                                        <span className='warning-icon'></span>
                                        <WrappedMessage message={messages.deleteWarningText2}/>
                                    </div>
                                    <Button
                                        className='delete-account-button'
                                        btnStyle='outline'
                                        onClick={this.showDeleteAccountModal}
                                        label={messages.deleteButton}
                                    />
                                </div>
                            </div>
                        </SettingsSection>
                    </div>
                </div>
                {this.renderModals()}
            </StandardPageLayout>
        );
    }

    private showDateOfBirthEdit = () => {
        this.setState({toggleDateOfBirthEdit: true, newDateOfBirthValue: this.state.dateOfBirthValue});
    }
    private cancelDateOfBirthEdit = () => {
        this.setState({toggleDateOfBirthEdit: false});
    }
    private handleChangeDateOfBirth = (date?: CalendarDate) => {
        this.setState({newDateOfBirthValue: date});
    }
    private handleSaveDateOfBirth = async () => {
        if (this.state.newDateOfBirthValue === undefined) {
            return;
        }
        try {
            await UsersApi.profilesPartialUpdate({
                id: this.props.loggedInUsername,
                data: {date_of_birth: this.state.newDateOfBirthValue.isoString},
            });
            this.setState({
                dateOfBirthValue: this.state.newDateOfBirthValue,
                toggleDateOfBirthEdit: false,
            });
        } catch (err) { showErrorMessage('Unable to save date of birth', {exception: err}); }
    }

    @bind private showDeleteAccountModal() {
        this.setState({deleteAccountModal: DeleteAccountModal.ConfirmDeleteFirst});
    }

    @bind private async performDeleteAccount() {
        try {
            // Delete the account
            await UsersApi.deleteAccount();

            // Redirect to the FAQ page, after logout complete.
            LoginActions.logout(ROUTES.General.FAQ);
        } catch (err) {
            showErrorMessage('Error occurred while deleting account.', {exception: err});
        }
    }

    private renderHeader() {
        return (
            <div className='settings-page-header-content extended'>
                <h1 className='account-settings-title'>
                    <WrappedMessage message={messages.AccountSettingsTitle}/>
                </h1>
            </div>
        );
    }

    private renderModals() {
        switch (this.state.deleteAccountModal) {
            case DeleteAccountModal.None:
                return;
            case DeleteAccountModal.ConfirmDeleteFirst:
                return(
                    <ModalConfirmation
                        onCanceled={() => this.setState({deleteAccountModal: DeleteAccountModal.None})}
                        onConfirmed={() => this.setState({deleteAccountModal: DeleteAccountModal.ConfirmDeleteFinal})}
                        modalClassNames='delete-account-modal'
                        title={messages.deleteAccountModalTitleFirst}
                        body={messages.deleteAccountModalConfirmationFirst}
                        confirmButtonText={messages.deleteAccountModalConfirmButtonFirst}
                        cancelButtonText={uiMessages.uiCancelButton}
                        closeButtonText={uiMessages.uiCancelButton}
                    />
                );
            case DeleteAccountModal.ConfirmDeleteFinal:
                return (
                    <ModalConfirmation
                        onCanceled={() => this.setState({deleteAccountModal: DeleteAccountModal.None})}
                        onConfirmed={() => this.setState({deleteAccountModal: DeleteAccountModal.PerformDeleteAccount})}
                        confirmClassNames='btn-danger'
                        modalClassNames='delete-account-modal'
                        title={messages.deleteAccountModalTitleFinal}
                        body={messages.deleteAccountModalConfirmationFinal}
                        confirmButtonText={messages.deleteAccountModalConfirmButtonFinal}
                        cancelButtonText={uiMessages.uiCancelButton}
                        closeButtonText={uiMessages.uiCancelButton}
                    />
                );
            case DeleteAccountModal.PerformDeleteAccount:
                this.performDeleteAccount();
                return;
            default: return;
        }
    }
}

export const UserProfileSettings = connect<ReduxStateProps, RootState>(
    (state: RootState) => ({
        loggedInUsername: getUsername(state),
        isLoggedIn: getLoggedInStatus(state),
    }),
)(UserProfileSettingsInternal);
